import { type AlbumCollectionModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type AlbumCollectionsSortState } from '../../lib/album-collections-query-model'
import {
  $albumCollectionsTablePage,
  $albumCollectionsTablePageSize,
  $albumCollectionsTableSort,
  albumCollectionsTablePageChanged,
  albumCollectionsTablePageReset,
  albumCollectionsTablePageSizeChanged,
  albumCollectionsTableQuery,
  albumCollectionsTableSortChanged,
  albumCollectionsTableSortReset,
} from '../../model/album-collections-table.model'
import { useAlbumCollectionsTableColumns } from './useAlbumCollectionsTableColumns'

interface AlbumCollectionsTableProps {
  onEdit: (albumCollectionId: string) => void
}
export const AlbumCollectionsTable = ({
  onEdit,
}: AlbumCollectionsTableProps) => {
  const { data, pending } = useUnit(albumCollectionsTableQuery)

  const sort = useUnit($albumCollectionsTableSort)
  const changeSort = useUnit(albumCollectionsTableSortChanged)
  const resetSort = useUnit(albumCollectionsTableSortReset)

  const page = useUnit($albumCollectionsTablePage)
  const changePage = useUnit(albumCollectionsTablePageChanged)
  const resetPage = useUnit(albumCollectionsTablePageReset)
  const pageSize = useUnit($albumCollectionsTablePageSize)
  const changePageSize = useUnit(albumCollectionsTablePageSizeChanged)

  const tableColumns: TableProps<AlbumCollectionModel>['columns'] =
    useAlbumCollectionsTableColumns({ onEdit, sort })

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<AlbumCollectionModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as AlbumCollectionsSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Выпусков на странице' },
        }}
      />
    </TableWrapper>
  )
}
